import { useEffect, useState, type ReactNode } from 'react';
import { Button, Stack, Table } from '@chakra-ui/react';
import { BRIDGE_LEVEL, type Bridge } from '@casa/bridge';
import { describeSource, readSource } from '../data/dashboard.ts';
import { appBuild, appVersion } from '../diagnostics/app_version.ts';
import { collectDiagnostics } from '../diagnostics/diagnostics.ts';
import { LogSettings } from './log_settings.tsx';
import { SettingsGroup, SettingsRow } from './settings_ui.tsx';
import { openPage } from '../ui/route.ts';

// "Diagnostics" (docs/specs/settings.md §4.5): what this device is and runs (DI-1), the log and
// where it may be sent, and "Send to support", a placeholder until it is built (DI-2, deferred by the
// owner). The engine and network tests of the spike are on their own page (/diagnostics).

export function DiagnosticsPart({ shell }: { shell: Bridge }) : ReactNode {
    const [rows, setRows] = useState<[string, string][]>([]);

    useEffect(() => {
        void (async () => {
            const diagnostics = collectDiagnostics(globalThis as unknown as Record<string, unknown>);
            const info = await shell.device.info().catch(() => undefined);
            const source = await readSource(shell);
            setRows([
                // The bundle's build and the bridge levels, for app updates (app-updates AU-28).
                ['Tilez', `${appVersion}, build ${appBuild}, bridge level ${BRIDGE_LEVEL}`],
                [
                    'Host',
                    diagnostics.host
                        ? `${diagnostics.host.shell} ${diagnostics.host.shellVersion}, bridge level ${diagnostics.host.bridgeLevel ?? 'none'}`
                        : 'a browser, through the Tilez Hub',
                ],
                ['Device', info ? `${info.model} (${info.platform} ${info.osVersion})` : 'unknown'],
                ['Screen', `${screen.width} × ${screen.height}, ${Math.round(devicePixelRatio * 100) / 100}× pixels`],
                ['Dashboard', source ? describeSource(source) : 'none yet'],
                ['Engine', diagnostics.userAgent],
            ]);
        })();
    }, [shell]);

    return (
        <Stack gap="6">
            <SettingsGroup title="This device">
                <Table.Root size="sm" variant="line">
                    <Table.Body>
                        {rows.map(([label, value]) => (
                            <Table.Row key={label}>
                                <Table.Cell fontWeight="medium" whiteSpace="nowrap" verticalAlign="top" ps="4">
                                    {label}
                                </Table.Cell>
                                <Table.Cell overflowWrap="anywhere">{value}</Table.Cell>
                            </Table.Row>
                        ))}
                    </Table.Body>
                </Table.Root>
            </SettingsGroup>
            <SettingsGroup note="Sending the diagnostics to support isn't available yet.">
                <SettingsRow
                    icon="paper-plane"
                    title="Send to support"
                    end={
                        <Button size="sm" disabled>
                            Send
                        </Button>
                    }
                />
                <SettingsRow icon="stethoscope" title="Engine and network tests" detail="Codecs, sockets and the bridge, for developers" onClick={() => openPage('/diagnostics')} />
            </SettingsGroup>
            <LogSettings shell={shell} />
        </Stack>
    );
}
